import { all, call, cancelled, put, select, takeEvery, takeLatest } from 'typed-redux-saga';

import { logError } from 'src/loggingManager';
import { RELOAD } from 'src/store/actions/actionTypes';
import { RootState } from 'src/store';
import { getSurfaceProperties } from 'src/selectors/pages/editor';
import { retrieveDesignData, RetrieveDesignDataParams } from 'src/services/disClient';
import { fetchProductModel } from 'src/services/productModelClient';
import {
    findLinkedMerchandisedProduct,
    resolveLinkedSizeAttributes,
} from 'src/services/linkedMerchandisedProductClient';
import { resolveEffectiveSku } from 'src/util/skuKey';
import type {
    DesignDataResultType,
    LinkedMerchandisedProductType,
    LinkedSizeAttributesType,
} from 'src/types/decorationSpecs';
import type { GenericSelectorProductType } from 'src/types/product';
import { applyVariables, changeEditorSkuSuccess } from 'src/pages/editor/components/surfaces/slice';
import { productHasSizeOption, productDeclaresBodyMeasurement } from './productPropertySizeChart';
import {
    retrieveDecorationSpecs,
    retrieveDecorationSpecsSettled,
    decorationSpecsFetchFailed,
    requestProductModel,
    productModelResolved,
    productModelFetchFailed,
    linkedProductResolved,
    requestLinkedSizeAttributes,
    linkedSizeAttributesResolved,
    clearLinkedSizeAttributes,
} from './slice';

const getCacheKey = (state: RootState) => state.pages.editor.decorationSpecs.cacheKey;
const getProductModelKey = (state: RootState) => state.pages.editor.decorationSpecs.productModelKey;
// TODO(linked-product-fallback): remove these 2 selectors alongside the rest of this fallback —
// see `linkedMerchandisedProductClient.ts`'s module doc.
const getLinkedProduct = (state: RootState) => state.pages.editor.decorationSpecs.linkedProduct;
const getLinkedSizeAttributesKey = (state: RootState) => state.pages.editor.decorationSpecs.linkedSizeAttributesKey;

function buildCacheKey({
    cimId,
    cimVersion,
    variables,
}: {
    cimId: string;
    cimVersion?: number;
    variables?: Record<string, string | undefined>;
}): string {
    return `${cimId}-${cimVersion}-${JSON.stringify(variables ?? {})}`;
}

/** Resolves the DIS fetch's result, converting a real (non-404/400) error into a message rather
 * than letting an uncaught rejection abort `fetchDecorationSpecs` entirely. */
function* fetchDesignDataSafely(
    params: RetrieveDesignDataParams,
): Generator<any, { data?: DesignDataResultType; error?: string }, any> {
    try {
        const data = yield* call(retrieveDesignData, params);
        return { data };
    } catch (error: any) {
        logError(`Failed to fetch DIS design data for decoration specs panel: ${error}`);
        return { error: (error && error.message) || 'Failed to fetch decoration specs' };
    }
}

// TODO(linked-product-fallback): `fetchProductModelIfNeeded` itself predates this fallback and
// stays after it's removed (it's the primary CIM-property fetch, not fallback-only) — but its
// *extended* shape below is fallback-motivated and should revert alongside the rest of it: narrow
// `ProductModelFetchResultType` back to just `{ modelKey, productModel }` (drop `sku` — nothing
// else needs it), change the return type back to `Generator<any, void, any>`, drop the `return`
// statements' object literals (back to a bare `return`/no return value), and drop the `|| !sku`
// half of the guard below (added solely so this type's `sku` field is never empty — see
// `linkedMerchandisedProductClient.ts`'s module doc for the rest of what to remove alongside this).
export type ProductModelFetchResultType = {
    modelKey: string;
    sku: string;
    productModel: GenericSelectorProductType | null;
};

/**
 * Fetches the full product model definition (options and property rules/assignments) from api.products.cimpress.io
 * if it hasn't been fetched for the current effective SKU and version yet.
 *
 * Returns what it fetched (or `undefined` when it skipped — nothing new to report), so
 * `fetchLinkedProductIfNeeded` can act on *exactly* the model this call just resolved, rather than
 * re-reading global state after the async gap. That distinction matters under `changeEditorSkuSuccess`'s/
 * `RELOAD`'s `takeEvery` (not `takeLatest`): two overlapping executions (e.g. switching products
 * twice quickly) would otherwise let a slower, older execution read the *newer* CIM's
 * `productModelKey` paired with that newer CIM's *not-yet-resolved* `productModel` (still `null`
 * at that instant) — wrongly and permanently caching "no Width/Length, no linked product" for the
 * new CIM before its own correct, later-resolving execution ever gets a chance to run (see
 * `fetchLinkedProductIfNeeded`'s own guard, which only fires once per `productModelKey`).
 */
export function* fetchProductModelIfNeeded(): Generator<any, ProductModelFetchResultType | undefined, any> {
    const { sku, skuVersion, merchantSku, merchantVersion } = yield* select(getSurfaceProperties);
    const { effectiveSku, effectiveVersion } = resolveEffectiveSku({
        sku,
        skuVersion,
        merchantSku,
        merchantVersion,
    });

    // `|| !sku` (as opposed to just `!effectiveSku`, this function's only pre-fallback condition)
    // exists solely so `ProductModelFetchResultType.sku` below is never empty for
    // `fetchLinkedProductIfNeeded`'s sake — see the TODO above this type.
    if (!effectiveSku || !sku) {
        return undefined;
    }

    const modelKey = effectiveVersion !== undefined ? `${effectiveSku}-${effectiveVersion}` : effectiveSku;
    const currentModelKey = yield* select(getProductModelKey);
    if (currentModelKey === modelKey) {
        // Already fetched (or already in flight, since `requestProductModel` below sets this
        // optimistically before the request resolves) for this exact product/version — whichever
        // execution owns that in-flight/already-settled fetch is responsible for
        // `fetchLinkedProductIfNeeded` too, not this one.
        return undefined;
    }

    // Set optimistically before the request resolves, so a slower/superseded response can be
    // told apart from the current one in `productModelResolved` (see that reducer's comment).
    yield* put(requestProductModel({ productModelKey: modelKey }));

    try {
        const productModel: GenericSelectorProductType | null = yield* call(fetchProductModel, {
            productId: effectiveSku,
            version: effectiveVersion,
        });
        yield* put(productModelResolved({ productModelKey: modelKey, productModel }));
        return { modelKey, sku, productModel };
    } catch (error: any) {
        // Deliberately `productModelFetchFailed`, not `productModelResolved({ productModel: null })`
        // — the latter is reserved for a confirmed-absent product (`fetchProductModel` already
        // converts a 404 into `null` without throwing), so dispatching it here too would make a
        // transient failure indistinguishable from "confirmed, there is no product model" and
        // permanently cache it as such. Resetting the key instead lets the next
        // `changeEditorSkuSuccess`/`RELOAD` for this exact CIM/version retry (see that action's own
        // doc comment).
        logError(`Failed to fetch product model for ${modelKey}: ${error}`);
        yield* put(productModelFetchFailed({ productModelKey: modelKey }));
        return undefined;
    }
}

// TODO(linked-product-fallback): remove alongside the rest of this fallback — see
// `linkedMerchandisedProductClient.ts`'s module doc.
/**
 * Resolves the linked-merchandised-product Width/Length fallback's *product* (not its
 * size-specific values yet — see `fetchLinkedSizeAttributesIfNeeded` for that) when the current
 * CIM doesn't declare Width/Length itself. See `productPropertySizeChart.ts`'s module doc for why
 * this fallback exists at all.
 *
 * Takes `fetchProductModelIfNeeded`'s own result directly (see that function's doc comment for
 * why re-selecting from global state here instead would be unsafe) — a no-op when that was
 * `undefined` (nothing new fetched this time around).
 */
export function* fetchLinkedProductIfNeeded(
    productModelResult: ProductModelFetchResultType | undefined,
): Generator<any, void, any> {
    if (!productModelResult) {
        return;
    }

    const { modelKey: productModelKey, sku, productModel } = productModelResult;

    if (productDeclaresBodyMeasurement(productModel) || !productHasSizeOption(productModel)) {
        // Either the CIM already covers Width/Length itself (no fallback needed), or it has no
        // `Size` option at all (a body measurement couldn't plausibly vary by size, so a
        // Product Search call here would just be wasted — see `productHasSizeOption`'s own doc).
        yield* put(linkedProductResolved({ productModelKey, linkedProduct: null }));
        return;
    }

    try {
        const linkedProduct: LinkedMerchandisedProductType | null = yield* call(findLinkedMerchandisedProduct, sku);
        yield* put(linkedProductResolved({ productModelKey, linkedProduct }));
    } catch (error: any) {
        // Deliberately `productModelFetchFailed`, not `linkedProductResolved({ linkedProduct: null })`
        // — the latter would cache this failure as a confirmed "no linked product" answer,
        // indistinguishable from a genuine none-found, and (since `linkedProduct` is only
        // re-attempted when `fetchProductModelIfNeeded` returns a *fresh* result — i.e. once per
        // `productModelKey`) nothing would ever retry it. Resetting `productModelKey` itself
        // forces a full refetch (product model + this lookup) on the next
        // `changeEditorSkuSuccess`/`RELOAD` for this CIM/version — slightly wasteful (the product
        // model fetch, which didn't fail, gets redone too) but safe and simple: it reuses the
        // exact mechanism `fetchProductModelIfNeeded`'s own failure path already needs, rather
        // than re-reading `productModel`/`productModelKey` from live state here (which is exactly
        // the pattern this function was changed to avoid — see this function's own doc comment).
        logError(`Failed to resolve a linked merchandised product for ${sku}: ${error}`);
        yield* put(productModelFetchFailed({ productModelKey }));
    }
}

// TODO(linked-product-fallback): remove alongside the rest of this fallback — see
// `linkedMerchandisedProductClient.ts`'s module doc.
/**
 * Resolves Width/Length for the currently selected `Size`, from the linked-merchandised-product
 * fallback resolved by `fetchLinkedProductIfNeeded` — a no-op until that's found one. See
 * `LinkedSizeAttributesType`'s own doc comment for why this only ever covers one size.
 */
export function* fetchLinkedSizeAttributesIfNeeded(): Generator<any, void, any> {
    const { merchantVariables } = yield* select(getSurfaceProperties);
    const linkedProduct = yield* select(getLinkedProduct);
    const size = merchantVariables?.Size;

    if (!linkedProduct || !size) {
        // No fallback needed, or the designer just removed `Size` entirely. Clear any
        // previously-resolved value immediately rather than leaving it displayed as if it still
        // applies to the current (now Size-less, or no-longer-fallback-needing) selection — the
        // exact failure mode `retrieveDecorationSpecs`'s own immediate-clear-on-selection-change
        // handling exists to avoid (see that reducer's comment) for decoration placement/white
        // ink; this fallback needs the same treatment since nothing else clears it on removal.
        //
        // Pass the key observed *here*, not a bare clear — `clearLinkedSizeAttributes`'s own
        // guard (slice.ts) only clears if this is still the current key by the time the dispatch
        // lands. Without that, this execution's determination ("nothing to resolve for what I just
        // read") could race a *different*, concurrently-running chain (e.g. a `changeEditorSkuSuccess`
        // `takeEvery` execution) that set a new, legitimate key in the gap between this `select`
        // and this `put`, and wipe out that unrelated chain's still-valid data.
        const currentKey = yield* select(getLinkedSizeAttributesKey);
        if (currentKey !== undefined) {
            yield* put(clearLinkedSizeAttributes({ linkedSizeAttributesKey: currentKey }));
        }
        return;
    }

    const linkedSizeAttributesKey = `${linkedProduct.productId}-${linkedProduct.version}-${size}`;
    const currentKey = yield* select(getLinkedSizeAttributesKey);
    if (currentKey === linkedSizeAttributesKey) {
        return;
    }

    // Set optimistically before the request resolves, mirroring `requestProductModel`'s own
    // pattern — but unlike that one, this generator runs under `takeLatest` (via
    // `fetchDecorationSpecsAndLinkedAttributes`), which *cancels* an in-flight run outright
    // (rather than merely superseding it) as soon as a newer `applyVariables` fires, even for a
    // change unrelated to `Size` (e.g. `Color`). `linkedSizeAttributesKey` only encodes
    // `(linkedProduct, size)`, not every variable, so such a cancellation can leave the key
    // "requested" with no settled result ever following it — and since the next run's own key is
    // unchanged (Size didn't move), the guard above would mistake that dangling "requested" key
    // for an already-resolved one and skip retrying forever. The `finally`/`cancelled()` check
    // below clears it back out on cancellation specifically, so a later run with this exact key
    // retries instead of being starved.
    yield* put(requestLinkedSizeAttributes({ linkedSizeAttributesKey }));

    try {
        const resolved = yield* call(resolveLinkedSizeAttributes, linkedProduct, size);
        const linkedSizeAttributes: LinkedSizeAttributesType = { size, ...resolved };
        yield* put(linkedSizeAttributesResolved({ linkedSizeAttributesKey, linkedSizeAttributes }));
    } catch (error: any) {
        // Deliberately `clearLinkedSizeAttributes`, not `linkedSizeAttributesResolved({ linkedSizeAttributes: null })`
        // — the latter would cache this failure as a confirmed "no Width/Length for this size"
        // answer, indistinguishable from a genuine none-found, and the key-match guard above would
        // then treat the failed attempt as already resolved. Clearing the key instead means the
        // very next `applyVariables` (any variable, since this generator re-runs on every one)
        // recomputes the same key, sees it no longer matches, and retries.
        logError(`Failed to resolve linked size attributes for ${linkedProduct.productId}/${size}: ${error}`);
        yield* put(clearLinkedSizeAttributes({ linkedSizeAttributesKey }));
    } finally {
        if (yield* cancelled()) {
            // Pass *this execution's own* key, guarded the same way as above: if a different,
            // concurrently-running chain (e.g. a `changeEditorSkuSuccess` `takeEvery` execution,
            // which `takeLatest` has no power to cancel) has since set a different, legitimate key,
            // this cancellation must not clear it out from under that chain.
            yield* put(clearLinkedSizeAttributes({ linkedSizeAttributesKey }));
        }
    }
}

/**
 * Fetches decoration-placement/print-white data from DIS for the current product/selection.
 * DIS Gearbox rules are keyed strictly on CIM ID, CIM version, CIM properties, and CIM surfaces.
 */
export function* fetchDecorationSpecs(): Generator<any, void, any> {
    const { sku, skuVersion, merchantVariables } = yield* select(getSurfaceProperties);

    // CIM ID is stored in `sku`.
    const cimId = sku;
    const cimVersion = skuVersion;

    // Nothing to look up yet (no CIM product chosen/translated yet).
    if (!cimId) {
        return;
    }

    // Deliberately `merchantVariables`, not `variables` — `applyVariables` (dispatched directly
    // by the variable picker) updates `merchantVariables` synchronously and wholesale on every
    // pick/removal, so it always reflects exactly what's currently selected, including a
    // just-removed property. `variables` is instead refreshed by a separate saga
    // (`surfaces/saga.ts`'s `getSurfaces`) only once the picker's own selection is fully resolved,
    // and only after an async round trip — so it can sit stale (reflecting a previous, now-wrong
    // complete selection) for an arbitrary stretch after a property changes, which showed up live
    // as decoration-placement data not clearing on removal and surfacing stale values after a new
    // complete pick. `merchantVariables` has no such lag.
    const cacheKey = buildCacheKey({ cimId, cimVersion, variables: merchantVariables });
    const currentCacheKey = yield* select(getCacheKey);
    if (currentCacheKey === cacheKey) {
        // Product identity and every variable are unchanged since the last fetch for this
        // selection — nothing new to look up. Refetching on every unrelated action (e.g. a surface
        // page switch) would be a wasted DIS call.
        return;
    }

    yield* put(retrieveDecorationSpecs({ cacheKey }));

    // Call DIS directly and trust its own signaling for an incomplete selection (400 with
    // `missingSelections`) or no usable data (404) — see `disClient.ts`. A prior proactive
    // pre-check against this CIM's full declared option list was removed: that list includes
    // every option/property the CIM declares (including static, non-merchandising attributes
    // like Brand/Material, and sub-options that only apply when a sibling toggle like
    // "Deco 2 Enabled" is on), which is a much larger set than what the picker ever resolves into
    // `merchantVariables` — so the pre-check could never be satisfied and permanently blocked the
    // DIS call for CIM-only products. DIS resolving rules directly from CIM properties is the
    // authoritative source now, so there's no separate narrower axis list for Scene Maker to
    // check completeness against.
    const designResult = (yield* call(fetchDesignDataSafely, {
        cimId,
        cimVersion,
        variables: merchantVariables,
    })) as {
        data?: DesignDataResultType;
        error?: string;
    };

    if (designResult.error) {
        // Deliberately `decorationSpecsFetchFailed`, not `retrieveDecorationSpecsSettled` with
        // this error attached — the latter would cache this failure as settled for `cacheKey`,
        // indistinguishable (to the `cacheKey` guard above) from a confirmed "no data" answer, and
        // permanently skip a retry until some variable changes — even if the designer picks the
        // exact same selection again later. Resetting `cacheKey` instead means the next
        // `applyVariables` for this exact selection retries rather than reusing the failure.
        yield* put(decorationSpecsFetchFailed({ cacheKey }));
        return;
    }

    yield* put(
        retrieveDecorationSpecsSettled({
            cacheKey,
            decorationPlacement: designResult.data?.decorationPlacement ?? null,
            printWhite: designResult.data?.printWhite ?? null,
            decorationMissingSelections: designResult.data?.missingSelections ?? null,
        }),
    );
}

// TODO(linked-product-fallback): once this fallback is removed, this function collapses back to
// a bare `yield* call(fetchDecorationSpecs)` (or can be inlined/deleted entirely) — see
// `linkedMerchandisedProductClient.ts`'s module doc.
/** Runs the linked-size-attributes fallback fetch and the DIS fetch *concurrently* — neither
 * depends on the other's result (`fetchDecorationSpecs` only reads `sku`/`skuVersion`/
 * `merchantVariables`; `fetchLinkedSizeAttributesIfNeeded` only reads `merchantVariables`/
 * `linkedProduct` and writes `linkedSizeAttributes*` state), so running them in series would mean
 * one blocking the other for no reason — specifically, a slow/hung Selector API call would delay
 * the DIS-derived decoration-placement panel, which has nothing to do with body Width/Length.
 * `yield* all([...])` runs both at once and still cancels both together if a newer `applyVariables`
 * fires (see the `takeLatest` comment on this saga's own watcher below) — same cancellation
 * semantics as the sequential version this replaced, without the serialized latency. */
export function* fetchDecorationSpecsAndLinkedAttributes(): Generator<any, void, any> {
    yield* all([call(fetchLinkedSizeAttributesIfNeeded), call(fetchDecorationSpecs)]);
}

export default function* decorationSpecsSaga() {
    return yield* all([
        // takeLatest, not takeEvery: without a completeness pre-check (removed above), every
        // single option pick now reaches DIS directly. A rapid sequence of picks previously
        // relied on the pre-check to skip most of the resulting network calls; takeLatest
        // bounds call volume to roughly one in-flight DIS request at a time by cancelling an
        // older, now-superseded fetch as soon as a newer applyVariables fires, rather than
        // leaving it to complete and be discarded by retrieveDecorationSpecsSettled's cacheKey
        // guard.
        yield* takeLatest(applyVariables, fetchDecorationSpecsAndLinkedAttributes),
        yield* takeEvery(changeEditorSkuSuccess, function* () {
            const productModelResult = yield* call(fetchProductModelIfNeeded);
            // TODO(linked-product-fallback): remove this call alongside the rest of this
            // fallback — see `linkedMerchandisedProductClient.ts`'s module doc.
            yield* call(fetchLinkedProductIfNeeded, productModelResult);
            yield* call(fetchDecorationSpecsAndLinkedAttributes);
        }),
        yield* takeEvery(RELOAD, function* () {
            const productModelResult = yield* call(fetchProductModelIfNeeded);
            // TODO(linked-product-fallback): remove this call alongside the rest of this
            // fallback — see `linkedMerchandisedProductClient.ts`'s module doc.
            yield* call(fetchLinkedProductIfNeeded, productModelResult);
            yield* call(fetchDecorationSpecsAndLinkedAttributes);
        }),
    ]);
}
